import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Button } from './components/Button';
import { Card, CardContent } from './components/Card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/Accordion';
import { Badge } from './components/Badge';
import { Check, Star, Gift, ArrowRight, Sparkles, BookOpen, Clock, Target, Users, ShieldCheck, Download, Infinity as InfinityIcon, Instagram, Mail, Smartphone, Lock, CreditCard, X, ArrowDown } from 'lucide-react';
import { TestimonialsMarquee } from './components/TestimonialsMarquee';
import { SocialProofNotifications } from './components/SocialProofNotifications';
import { Countdown } from './components/Countdown';
import { TomosSlider } from './components/TomosSlider';
import { BonusSlider } from './components/BonusSlider';
export function App() {
  return <div className="min-h-screen w-full bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground antialiased" data-id="element-0" data-mp="23w4gi6fape3h">
      {/* Social proof notifications (fixed bottom-left, non-intrusive) */}
      <SocialProofNotifications data-id="element-1" data-mp="3mk85uxa7a9d0" />

      {/* 1. Sticky top nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md" data-id="element-2" data-mp="3rbr9en3irte8">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-3" data-mp="67znpl2q47d">
          <div className="font-heading font-bold text-base md:text-xl tracking-widest text-primary whitespace-nowrap" data-id="element-4" data-mp="2zllff2q1qmn9">
            Sistema PRO COSMOS
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-muted-foreground" data-id="element-5" data-mp="33orla998ybt7">
            <a href="#tomos" className="hover:text-primary transition-colors" data-id="element-6" data-mp="38o4d78ez692n">
              Tomos
            </a>
            <a href="#bonus" className="hover:text-primary transition-colors" data-id="element-7" data-mp="nvsub5qlapwh">
              Bonus
            </a>
            <a href="#faq" className="hover:text-primary transition-colors" data-id="element-8" data-mp="stue5svuat90">
              FAQ
            </a>
          </div>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_0_15px_rgba(212,168,87,0.3)]" data-id="element-9" data-mp="356rh3pi3e8j">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-10" data-mp="d3mlbefwx1lq">
              Acceder Ahora
            </a>
          </Button>
        </div>
      </nav>

      {/* 2. Hero */}
      <section className="relative pt-3 pb-3 md:pt-8 md:pb-8 overflow-hidden" data-id="element-11" data-mp="3bmthr6pqrq1t">
        <div className="absolute inset-0 pointer-events-none" data-id="element-12" data-mp="3qb7jtffc3ahb">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-primary/[0.06] rounded-full blur-3xl" data-id="element-13" data-mp="3qcimtd0yge7d"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-4xl text-center" data-id="element-14" data-mp="2xudaftpt55v3">
          <Badge variant="outline" className="mb-3 border-primary/50 text-primary uppercase tracking-widest px-4 py-1.5 text-xs font-semibold bg-primary/5 backdrop-blur-sm" data-id="element-15" data-mp="2svtr812lm86f">
            EL SISTEMA QUE ORGANIZA TU ASTROLOGÍA
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.1] mb-5 text-foreground" data-id="element-16" data-mp="1d6kkm9tu85l5">
            Dejá de mirar cartas natales{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-17" data-mp="13fgapoj0rax6">
              sin saber por dónde empezar.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-foreground/85 mb-7 leading-relaxed max-w-2xl mx-auto" data-id="element-18" data-mp="1w3lvgba2ozx0">
            Sistema completo de informes astrológicos listos para entregar. Todo
            tu flujo de trabajo resuelto en un solo{' '}
            <span className="text-primary font-semibold" data-id="element-19" data-mp="1m3cxarwit4ez">clic</span>.
          </p>

          {/* Static product mockup — visually aligned under subtitle */}
          <div className="relative flex justify-center -mt-[30%] -mb-[28%]" data-id="element-20" data-mp="aq99uiw3e98r">
            <motion.img src="https://cdn.magicpatterns.com/uploads/kvK3Hmp5jKBFG78v93siUa/Untitled_design_(4).png" alt="Sistema PRO COSMOS — colección completa de manuales y bonos de astrología" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="relative z-10 mx-auto w-[106.5%] max-w-none object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.22)]" data-id="element-21" data-mp="qwvr4d1d8l5u" />
          </div>

          {/* PRIMARY CTA — directly after mockup */}
          <div className="flex flex-col items-center justify-center gap-3 mb-4" data-id="element-22" data-mp="14v3tj0h68eoe">
            <Button asChild size="lg" className="relative w-full sm:w-auto text-lg md:text-xl px-10 py-8 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_50px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.04]" data-id="element-23" data-mp="zw0ovh53sagy">
              <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-24" data-mp="1w5ghv1xpig9j">
                QUIERO EL SISTEMA AHORA <ArrowRight className="ml-2 h-5 w-5" data-id="element-25" data-mp="254bnabsxfmbs" />
              </a>
            </Button>
            <a href="#tomos" className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline" data-id="element-26" data-mp="29wljg9kmximi">
              Ver contenido completo ↓
            </a>
          </div>
        </div>
      </section>

      {/* 2.1 Trust bar + minimalist statistics */}
      <section className="relative py-10 md:py-14 bg-card/20 overflow-hidden" data-id="element-27" data-mp="2ntiaoc4o123x">
        <div className="absolute inset-0 pointer-events-none" data-id="element-28" data-mp="2stwh5pqmc3zu">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-29" data-mp="tid8zdd52zr8"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-30" data-mp="21rkglqgey8a6">
          {/* Premium trust bar — always one row */}
          <motion.ul className="grid grid-cols-3 items-center gap-2 sm:gap-4 pb-5 md:pb-6" initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} data-id="element-31" data-mp="1wt95zdiantqw">
            {[{
            Icon: ShieldCheck,
            label: 'Garantía de 7 días'
          }, {
            Icon: Lock,
            label: 'Pago 100% seguro'
          }, {
            Icon: CreditCard,
            label: 'Mercado Pago | Tarjetas'
          }].map(({
            Icon,
            label
          }, i) => <li key={i} className="flex items-center justify-center gap-1.5 sm:gap-2.5 text-center" data-id="element-32" data-mp="3i3wcnl83fpza" data-mpi={i}>
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-33" data-mp="3n27n9y67q4ik" />
                <span className="text-[10px] sm:text-sm md:text-[15px] text-foreground/80 font-medium leading-tight" data-id="element-34" data-mp="3d38mt2vlpf6n">
                  {label}
                </span>
              </li>)}
          </motion.ul>

          {/* Subtle divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-border/60 to-transparent" aria-hidden="true" data-id="element-35" data-mp="2pez3c7lv13io"></div>

          {/* Minimalist statistics — always one row */}
          <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-6 md:pt-8" data-id="element-36" data-mp="2gdrc29hrh2y3">
            {[{
            num: '+1.000',
            label: 'Astrólogos confían'
          }, {
            num: '500+',
            label: 'Páginas de formación'
          }, {
            num: '4.9',
            label: 'Calificación',
            star: true
          }, {
            num: '6 + 10',
            label: 'Tomos + bonus'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.07 * i
          }} className="flex flex-col items-center text-center" data-id="element-37" data-mp="2hwj632rsq2ph" data-mpi={i}>
                <div className="flex items-baseline justify-center gap-1" data-id="element-38" data-mp="9owck6z8lyjw">
                  <span className="text-xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-heading font-black text-primary leading-none tracking-tight whitespace-nowrap" data-id="element-39" data-mp="9qz26pwh9po3">
                    {stat.num}
                  </span>
                  {stat.star && <Star className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-primary fill-primary shrink-0 translate-y-[-1px]" aria-hidden="true" data-id="element-40" data-mp="nnm9nmm23y89" />}
                </div>
                <div className="mt-1.5 md:mt-2 text-[8px] sm:text-[10px] md:text-xs text-muted-foreground font-medium uppercase tracking-[0.14em] leading-tight" data-id="element-41" data-mp="3r31iwlmm23sc">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 2.15 Opiniones — right after trust & statistics */}
      <TestimonialsMarquee data-id="element-42" data-mp="38dlgfsrd819a" />

      {/* 2.2 Emotional pain-points */}
      <section className="relative py-20 md:py-24 overflow-hidden" data-id="element-43" data-mp="2f7n35p2k05id">
        <div className="absolute inset-0 pointer-events-none" data-id="element-44" data-mp="2f9hlqwqcrlja">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-45" data-mp="2k4n7nvjr907d"></div>
        </div>

        <div className="container mx-auto px-4 max-w-2xl relative z-10" data-id="element-46" data-mp="2u4xjbbq55zkf">
          {/* Header */}
          <div className="text-center mb-10 md:mb-12" data-id="element-47" data-mp="1wh5kkdbfqqaq">
            <p className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-primary/70 mb-5 font-medium" data-id="element-48" data-mp="1wej6duu1hcjj">
              Sistema PRO COSMOS
            </p>
            <h2 className="font-heading font-bold text-foreground leading-tight" data-id="element-49" data-mp="26ds5f23rhhcr">
              <span className="block text-base md:text-xl tracking-wide uppercase text-foreground/70 mb-2" data-id="element-50" data-mp="2b5s1ukkmhs4l">
                Si después de tantos cursos...
              </span>
              <span className="block text-3xl md:text-5xl uppercase tracking-tight" data-id="element-51" data-mp="1ip8vyj8z6yj3">
                Todavía te pasa{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-52" data-mp="3vzs8do0ow4tk">
                  esto...
                </span>
              </span>
            </h2>
            <div className="mx-auto mt-7 h-px w-24 bg-gradient-to-r from-transparent via-primary/60 to-transparent" aria-hidden="true" data-id="element-53" data-mp="3myatua2xkb6z"></div>
          </div>

          {/* Pain point list */}
          <ul className="rounded-2xl bg-card/30 border border-border/40 backdrop-blur-sm px-5 md:px-8 divide-y divide-primary/10" data-id="element-54" data-mp="3d1fw3teheetr">
            {['Abrís una carta y no sabés qué mirar primero.', 'Saltás entre planetas y aspectos sin entender el panorama completo.', 'Sentís que estudiás mucho pero todo sigue desconectado.', 'Dudás constantemente de tus interpretaciones.', 'Te bloqueás frente a cartas reales.', 'No sabés cómo convertir toda la teoría en una sesión profesional.', 'Sentís que siempre te falta "algo más" para empezar.'].map((pain, i) => <li key={i} className="flex items-center gap-4 py-4 md:py-[18px]" data-id="element-55" data-mp="2kmsy0ao59o5g" data-mpi={i}>
                <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full border border-primary/40" aria-hidden="true" data-id="element-56" data-mp="26n1ejueuzfk2">
                  <X className="w-3 h-3 text-primary/80" strokeWidth={2} data-id="element-57" data-mp="2ye7iyn1m3zf5" />
                </span>
                <p className="text-sm md:text-base text-foreground/85 leading-relaxed" data-id="element-58" data-mp="33cr5opo15f8k">
                  {pain}
                </p>
              </li>)}
          </ul>

          {/* Transition */}
          <div className="text-center mt-12 md:mt-14" data-id="element-59" data-mp="2tmpgtvnurssl">
            <p className="font-heading font-bold text-xl md:text-3xl uppercase leading-tight text-foreground" data-id="element-60" data-mp="2tleg64ppf15p">
              ¿Hasta cuándo vas a seguir interpretando{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-61" data-mp="3m4bler32dt4k">
                sin un sistema
              </span>
              ?
            </p>
            <ArrowDown className="mx-auto mt-6 w-5 h-5 md:w-6 md:h-6 text-primary/70" strokeWidth={1.5} aria-hidden="true" data-id="element-62" data-mp="15oey0rf4vq0o" />
          </div>
        </div>
      </section>

      {/* 3. ¿Qué incluye la colección? — Tomos */}
      <section id="tomos" className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-63" data-mp="rpf71d45ez1c">
        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-64" data-mp="mrnfik7f1exo">
          <div className="text-center mb-14" data-id="element-65" data-mp="11oe8obqhxueu">
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-66" data-mp="11p65fpvtxy6p">
              ¿Qué incluye la{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-67" data-mp="2d2rt16m1jrq4">
                colección?
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-68" data-mp="2869pga2g5zfd">
              Cinco tomos estructurados para llevarte desde el caos mental hasta
              interpretaciones claras y profesionales.
            </p>
          </div>

          <TomosSlider data-id="element-69" data-mp="qlyf8pl5v7qg" />
        </div>
      </section>

      {/* 4. Bonus */}
      <section id="bonus" className="py-24 relative overflow-hidden" data-id="element-70" data-mp="vnnn5wd375ab">
        <div className="absolute inset-0 pointer-events-none" data-id="element-71" data-mp="10lxokopen24d">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-72" data-mp="10jl7edrh0vsd"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-73" data-mp="1acs83lb9nnm4">
          <div className="text-center mb-14" data-id="element-74" data-mp="1edlvmdksr0eh">
            <div className="flex justify-center mb-5" data-id="element-75" data-mp="1ecargcnfvqmu">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/50 backdrop-blur-sm shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-76" data-mp="1jcyhow3ug3g0">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" data-id="element-77" data-mp="1yfq7s1bj5x2k"></span>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-emerald-300" data-id="element-78" data-mp="1oh11l5szy125">
                  GRATIS por el día de hoy
                </p>
              </div>
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-79" data-mp="2h5omh09u2of6">
              Biblioteca{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-80" data-mp="2c7myq21gq0xd">
                COSMOS™
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-81" data-mp="23eterrb2mdd6">
              Incluidos sin costo adicional con tu compra de hoy.
            </p>
          </div>

          <BonusSlider data-id="element-82" data-mp="2v4xwtjbcyob8" />
        </div>
      </section>

      {/* 5. ¿Para quién es COSMOS? */}
      <section className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-83" data-mp="t7dvv9uvhhcp">
        <div className="absolute inset-0 pointer-events-none" data-id="element-84" data-mp="y7jqmywfvjz0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-85" data-mp="y935qm6czoz7"></div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-86" data-mp="ofw49ceqot4e">
          <div className="text-center mb-14" data-id="element-87" data-mp="agweg1fhq86n">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-88" data-mp="ag4ksw5gekj0">
              Hecho para vos
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-89" data-mp="jrsa1lnacpu">
              ¿Para quién es{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-90" data-mp="5j4hgszznvvl">
                COSMOS?
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-91" data-mp="1zv2nyn6wydns">
            {['Para quienes abren cartas natales y no saben qué mirar primero.', 'Para quienes estudian astrología hace tiempo pero sienten todo desconectado.', 'Para quienes quieren interpretar con claridad y seguridad real.', 'Para quienes desean transformar información suelta en sesiones profesionales.', 'Para quienes sienten que la astrología puede ser más simple cuando existe un sistema.'].map((item, i) => <div key={i} className={'group flex items-start gap-4 p-6 bg-background/60 border border-border/50 rounded-2xl hover:border-primary/40 hover:bg-background/80 transition-all backdrop-blur-sm ' + (i === 4 ? 'md:col-span-2' : '')} data-id="element-92" data-mp="2yg4sp7izyq8" data-mpi={i}>
                <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 border border-primary/30 group-hover:bg-primary/15 transition-colors" data-id="element-93" data-mp="2ipkr7oz7y551">
                  <Check className="w-5 h-5 text-primary" strokeWidth={2.5} data-id="element-94" data-mp="1rw0w1onoop3v" />
                </div>
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed" data-id="element-95" data-mp="1wukjjthxe8yc">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="relative pt-12 mt-12 text-center" data-id="element-96" data-mp="26lyfxsekysn9">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-97" data-mp="26njc1mgjmjn8"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-98" data-mp="190aprp8px6k6">
              "COSMOS fue creado para transformar{' '}
              <span className="font-semibold" data-id="element-99" data-mp="1iusyj8szrsau">caos mental</span> en{' '}
              <span className="font-semibold" data-id="element-100" data-mp="1nw8j6zr9bwsp">interpretaciones claras</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 8. Oferta especial */}
      <section className="py-24 relative overflow-hidden" data-id="element-101" data-mp="2za41ghdgklo5">
        <div className="absolute inset-0 pointer-events-none" data-id="element-102" data-mp="2z8j6wrr592qa">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" data-id="element-103" data-mp="nlz4ms8oooae"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-104" data-mp="dx7g6u2u0m48">
          <Card className="bg-card/80 backdrop-blur-xl border-primary/30 shadow-[0_0_40px_rgba(212,168,87,0.1)] overflow-hidden" data-id="element-105" data-mp="dv4hzmrizmof">
            <div className="bg-gradient-to-b from-primary/10 to-transparent px-6 pt-12 pb-10 md:px-10 md:pt-14 md:pb-12 text-center border-b border-primary/15" data-id="element-106" data-mp="3yrqmvjgdkwu">
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-5 text-foreground" data-id="element-107" data-mp="3w1ivjklvtuma">
                Tu sistema de interpretación{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-108" data-mp="3m5o4q27i8t8f">
                  empieza hoy.
                </span>
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-109" data-mp="1kd013rqy83s4">
                Pasá de sentirte perdida frente a una carta natal a interpretar
                con{' '}
                <span className="text-primary/90 font-medium" data-id="element-110" data-mp="279kh8w6kpfv6">estructura</span>,{' '}
                <span className="text-primary/90 font-medium" data-id="element-111" data-mp="22grfmh6einnb">claridad</span> y{' '}
                <span className="text-primary/90 font-medium" data-id="element-112" data-mp="1xizmxkxxmxi2">seguridad</span>.
              </p>

              <div className="flex justify-center mb-4" data-id="element-113" data-mp="1the1hb7c1aw9">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/8 border border-primary/30 text-primary/95 text-sm font-medium backdrop-blur-sm" role="status" data-id="element-114" data-mp="1tertjagj4tdi">
                  <span data-id="element-115" data-mp="1ekdj3yb16sag">✨</span>
                  <span data-id="element-116" data-mp="1el55ciiqndo2">
                    Quedan{' '}
                    <span className="font-heading font-bold text-primary mx-0.5" data-id="element-117" data-mp="3dx7wmexltcca">
                      12
                    </span>{' '}
                    accesos disponibles hoy
                  </span>
                </div>
              </div>

              <p className="text-xs md:text-sm text-muted-foreground/85 italic leading-relaxed max-w-xl mx-auto" data-id="element-118" data-mp="3hedgwgqiq7xl">
                Cada día más astrólogos están dejando de estudiar información
                suelta y empezando a interpretar con un sistema.
              </p>
            </div>

            <CardContent className="p-8 md:p-14 lg:p-16 bg-gradient-to-b from-card/40 to-card/60" data-id="element-119" data-mp="3v5ims0khbwgd">
              {/* PRICE HERO */}
              <div className="text-center mb-14" data-id="element-120" data-mp="32hcyx4y4prcb">
                <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-muted-foreground/80 mb-6 font-medium" data-id="element-121" data-mp="32i4ifhegbym8">
                  Todo el sistema completo por menos de lo que cuesta una sola
                  sesión.
                </p>

                <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-[0.2em] mb-8 shadow-[0_0_20px_rgba(212,168,87,0.3)]" data-id="element-122" data-mp="3cd6bgobulku3">
                  Hoy acceso completo
                </div>

                <p className="text-sm md:text-base text-muted-foreground mb-3" data-id="element-123" data-mp="22z9uaqmn11a">
                  Hoy pagás solo
                </p>

                <div className="relative inline-block mb-6" data-id="element-124" data-mp="bzvj4iocnr6w">
                  <div className="absolute inset-0 -m-8 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-125" data-mp="bx9b6hxjr9o5"></div>
                  <div className="relative text-7xl md:text-8xl lg:text-9xl font-heading font-black text-primary leading-none tracking-tight drop-shadow-[0_0_30px_rgba(212,168,87,0.5)]" data-id="element-126" data-mp="3fg2fe44fqm5n">
                    $19.900
                  </div>
                </div>

                <div className="flex flex-col items-center gap-3" data-id="element-127" data-mp="3p76qutuwjovq">
                  <Badge variant="outline" className="border-emerald-500/60 text-emerald-300 bg-emerald-500/10 px-5 py-2 text-sm md:text-base font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-128" data-mp="3kevu1zvgkj7e">
                    Ahorrás $109.500
                  </Badge>
                  <p className="text-base md:text-lg font-semibold text-foreground/95" data-id="element-129" data-mp="176cno10klik9">
                    Pago único · Acceso de por vida
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm text-primary/90 font-medium" data-id="element-130" data-mp="xbawz0qnc8fk">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-131" data-mp="12ae1mk2pscn0"></span>
                    Acceso inmediato tras la compra
                  </div>
                </div>
              </div>

              {/* PRIMARY CTA */}
              <div className="text-center mb-10" data-id="element-132" data-mp="1gyipe2gmjecn">
                <Button asChild size="lg" className="relative w-full text-lg md:text-2xl py-9 md:py-10 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_60px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.02]" data-id="element-133" data-mp="1gxpdyo6q00o6">
                  <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-134" data-mp="pslhwvloy6rr">
                    Desbloquear COSMOS
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-135" data-mp="fzns04eddfj4" />
                  </a>
                </Button>
              </div>

              {/* Trust badges — below CTA */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 mb-10" data-id="element-136" data-mp="18q5y7eae3x0r">
                {[{
                icon: '🔒',
                label: 'Pago seguro'
              }, {
                icon: '⚡',
                label: 'Acceso en 5 min'
              }, {
                icon: '🛡️',
                label: 'Garantía 7 días'
              }, {
                icon: '💳',
                label: 'Mercado Pago'
              }].map((badge, i) => <div key={i} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-background/40 border border-border/40 backdrop-blur-sm text-xs md:text-sm font-medium text-foreground/85" data-id="element-137" data-mp="1ux08qvw7nkv3" data-mpi={i}>
                    <span className="text-base shrink-0" aria-hidden="true" data-id="element-138" data-mp="28s2la1vkc8gv">
                      {badge.icon}
                    </span>
                    <span data-id="element-139" data-mp="2ionr2pfepnlr">{badge.label}</span>
                  </div>)}
              </div>

              {/* Countdown timer — right below trust badges */}
              <div className="mb-12" data-id="element-140" data-mp="2mr28r67cahbg">
                <Countdown data-id="element-141" data-mp="2rp3tdvkeyop1" />
              </div>

              <div className="text-center mb-12 space-y-1.5" data-id="element-142" data-mp="2rou121bwc5g6">
                <p className="text-sm md:text-base text-foreground/90 font-medium" data-id="element-143" data-mp="scriquf0dh43">
                  Hoy desbloqueás toda la colección COSMOS con un único acceso.
                </p>
                <p className="text-sm text-primary/90 font-semibold" data-id="element-144" data-mp="20lysbd1uezpo">
                  Quedan pocos accesos disponibles.
                </p>
              </div>

              <div className="flex items-center gap-4 mb-10" data-id="element-145" data-mp="1vozykvm3cus0">
                <div className="flex-1 h-px bg-border/40" data-id="element-146" data-mp="29n6o9ogvzogl"></div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60 font-medium" data-id="element-147" data-mp="24ntuejrllh4i">
                  Desglose
                </span>
                <div className="flex-1 h-px bg-border/40" data-id="element-148" data-mp="2jjjjwrvzscdz"></div>
              </div>

              <div className="space-y-3 mb-10 text-sm" data-id="element-149" data-mp="2el88wdtynvu6">
                {[{
                name: 'COSMOS I',
                price: '$19.900',
                isMain: true
              }, {
                name: 'COSMOS II',
                price: '$19.900'
              }, {
                name: 'COSMOS III',
                price: '$19.900'
              }, {
                name: 'COSMOS IV',
                price: '$19.900'
              }, {
                name: 'COSMOS V',
                price: '$19.900'
              }, {
                name: 'Bonus Exclusivos',
                price: '$29.900'
              }].map((item, i) => <div key={i} className="flex justify-between items-center py-1.5" data-id="element-150" data-mp="2y8317z67y1k8" data-mpi={i}>
                    <span className={item.isMain ? 'text-foreground/85 font-semibold' : 'text-muted-foreground/60'} data-id="element-151" data-mp="3302wvff9a8b0">
                      {item.name}
                      {item.isMain && <span className="ml-2 text-[10px] text-primary/70 font-medium uppercase tracking-wider" data-id="element-152" data-mp="2fbbe1enk32aw">
                          (principal)
                        </span>}
                    </span>
                    <div className="flex items-center gap-2.5" data-id="element-153" data-mp="2uqp2f8y8z8ry">
                      {item.isMain ? <span className="text-primary/90 font-semibold text-sm" data-id="element-154" data-mp="2pr2mgrr1befg">
                          {item.price}
                        </span> : <>
                          <span className="line-through text-muted-foreground/40 text-xs" data-id="element-155" data-mp="2gs8ujeonfc88">
                            {item.price}
                          </span>
                          <span className="text-primary/70 font-medium text-[10px] uppercase tracking-wider" data-id="element-156" data-mp="2714iamqcy5h3">
                            Bonificado
                          </span>
                        </>}
                    </div>
                  </div>)}

                <div className="flex justify-between items-center pt-4 mt-4 border-t border-border/40 text-sm" data-id="element-157" data-mp="1t1v39qdw1hep">
                  <span className="text-muted-foreground/70" data-id="element-158" data-mp="8k3rikkf2xcs">
                    Valor real total:
                  </span>
                  <span className="line-through text-muted-foreground/50" data-id="element-159" data-mp="cjuo54wmk7p8">
                    $129.400
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 10. Preguntas frecuentes */}
      <section id="faq" className="py-24 relative overflow-hidden" data-id="element-160" data-mp="3l1qt3zcgn48w">
        <div className="absolute inset-0 pointer-events-none" data-id="element-161" data-mp="3q022ya2r9er4">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-162" data-mp="3q0lk3okkzz43"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-163" data-mp="3kr3hwht7k8m">
          <div className="text-center mb-12" data-id="element-164" data-mp="32ahmpyxzkvlj">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-165" data-mp="329olfdhsqyas">
              Lo que querés saber
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-166" data-mp="2gznh6keoji8x">
              Preguntas{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-167" data-mp="2lz01axe62b7t">
                frecuentes
              </span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-168" data-mp="2bzssmnidn4r1">
            {[{
            q: '¿En qué formato recibo COSMOS?',
            a: 'Recibirás toda la colección en formato digital con acceso inmediato después de la compra. Podrás verla cuando quieras y avanzar a tu ritmo.'
          }, {
            q: '¿Puedo usarlo desde el celular?',
            a: 'Sí. Toda la colección está optimizada para celular, tablet y computadora para que puedas estudiar desde cualquier lugar.'
          }, {
            q: '¿Sirve si todavía me siento perdida interpretando?',
            a: 'Sí. COSMOS fue creado justamente para ayudarte a dejar de sentir caos frente a una carta natal y empezar a interpretar con estructura y claridad.'
          }, {
            q: '¿Los bonus están incluidos?',
            a: 'Sí. Al acceder hoy desbloqueás toda la colección completa junto con todos los bonus incluidos.'
          }, {
            q: '¿Cuándo obtengo acceso?',
            a: 'El acceso es inmediato. Apenas completes tu compra recibirás toda la colección automáticamente.'
          }, {
            q: '¿Tiene garantía?',
            a: 'Sí. Probá COSMOS durante 7 días. Si no cumple tus expectativas, te devolvemos el 100% de tu dinero.'
          }].map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl px-6 md:px-7 data-[state=open]:border-primary/40 data-[state=open]:bg-card/80 transition-all" data-id="element-169" data-mp="22cy9vwe6s0rz" data-mpi={i}>
                <AccordionTrigger className="text-left font-heading font-semibold text-base md:text-lg hover:text-primary py-6 leading-snug" data-id="element-170" data-mp="uymfuyt1v4f3">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 text-base pb-6 leading-relaxed" data-id="element-171" data-mp="18ru72pd7xxw9">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="relative pt-12 mt-12 text-center" data-id="element-172" data-mp="1ipidick6stu9">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-173" data-mp="1is4kob3617by"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-174" data-mp="1nn0hdojckive">
              "No necesitás memorizar{' '}
              <span className="font-semibold" data-id="element-175" data-mp="3phj4d8vj0sdo">más información</span>.
              <br className="hidden md:block" data-id="element-176" data-mp="3thtfezy6e8ze" />
              Necesitás un{' '}
              <span className="font-semibold" data-id="element-177" data-mp="3tjw9pw6d2o9x">sistema para entenderla</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 11. Final CTA section */}
      <section className="py-32 relative overflow-hidden bg-card border-t border-border/50 text-center" data-id="element-178" data-mp="3h6itdjolahjs">
        <div className="absolute inset-0 pointer-events-none" data-id="element-179" data-mp="3c6o3njgjvy0b">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" data-id="element-180" data-mp="3886tss5qw9f7"></div>
        </div>
        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-181" data-mp="3sxjs7hvndwy">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-foreground" data-id="element-182" data-mp="3qumd2eeaii7">
            Tu próxima interpretación comienza hoy.
          </h2>
          <p className="text-xl text-muted-foreground mb-12" data-id="element-183" data-mp="3qwllzirstthl">
            La diferencia entre sentirte perdida y sentir claridad comienza con
            un sistema.
          </p>
          <Button asChild size="lg" className="text-xl px-10 py-8 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(212,168,87,0.4)] transition-all hover:scale-105 w-full sm:w-auto" data-id="element-184" data-mp="2fhyk7op9yxc8">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-185" data-mp="c2cv8kqxhzfs">
              DESBLOQUEAR COSMOS
            </a>
          </Button>
        </div>
      </section>

      {/* 11.5 Librofy trust badge + Instagram + Email — moved to bottom */}
      <section className="py-16 relative border-t border-border/40" data-id="element-186" data-mp="uzmcpwcp12ue">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-187" data-mp="gzlcmj495zfz">
          <div className="flex flex-col items-center text-center" data-id="element-188" data-mp="gzbnsyuy1ybv">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground/70 mb-6 font-medium" data-id="element-189" data-mp="lxwmgtujs9y7">
              Una compra inteligente respaldada por
            </p>
            <a href="https://www.instagram.com/librofy.online" target="_blank" rel="noopener noreferrer" className="inline-block transition-all hover:-translate-y-1 hover:drop-shadow-[0_15px_30px_rgba(255,140,50,0.35)]" aria-label="Visitá nuestro Instagram" data-id="element-190" data-mp="36onfvlbh70dd">
              <img src="https://cdn.magicpatterns.com/uploads/e1ZfmCqnnwuhZanYAJfgbJ/logo_final_para_la_web.png" alt="Librofy — Compra Inteligente" className="h-[22rem] md:h-[28rem] w-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)]" data-id="element-191" data-mp="384rj2tme46d9" />
            </a>

            <p className="mt-6 mb-4 text-sm md:text-base text-foreground/80 max-w-xl" data-id="element-192" data-mp="2ak57h4wr5own">
              ✨ Consultanos sin compromiso. Respondemos tus mensajes en pocos
              minutos.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm" data-id="element-193" data-mp="2kf6xe2yuqv0a">
              <a href="https://www.instagram.com/librofy.online" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-194" data-mp="1wpch1bfbqoba">
                <Instagram className="h-4 w-4" data-id="element-195" data-mp="m7ua935sw7l" />
                <span data-id="element-196" data-mp="5er7h4xxppbv">@librofy.online</span>
              </a>
              <span className="hidden sm:inline text-muted-foreground/30" data-id="element-197" data-mp="5fapsmrhyfqf">
                •
              </span>
              <a href="mailto:soportelibrofy@gmail.com" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-198" data-mp="9i6zfnz271i8">
                <Mail className="h-4 w-4" data-id="element-199" data-mp="3dw5i58ao4hq9" />
                <span data-id="element-200" data-mp="3huut6u88hn6q">soportelibrofy@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 12. Footer */}
      <footer className="py-8 bg-background border-t border-border/40 text-center text-muted-foreground text-sm" data-id="element-201" data-mp="3msmmnsoj1hd1">
        <div className="container mx-auto px-4" data-id="element-202" data-mp="124i0t0uhp6g3">
          <p className="font-heading font-medium tracking-widest text-foreground/60 mb-2" data-id="element-203" data-mp="1236zd7oioas5">
            COSMOS™
          </p>
          <p data-id="element-204" data-mp="1cajatiqdg4h8">© 2026 Todos los derechos reservados</p>
        </div>
      </footer>
    </div>;
}